import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { numberToGrade } from '../utils/grades';
import './MarketplacePage.css';

interface Listing {
  id: string;
  seller_id: string;
  player_id: string;
  league_id: string;
  price: number;
  status: string;
  created_at: string;
  // Joined player data
  first_name?: string;
  last_name?: string;
  position?: string;
  age?: number;
  gender?: string;
  overall?: number;
  power?: number;
  contact?: number;
  speed?: number;
  discipline?: number;
  batting_hand?: string;
  throwing_hand?: string;
  velocity?: number;
  control?: number;
  movement?: number;
  // Seller
  seller_name?: string;
}

type SortKey = 'price_asc' | 'price_desc' | 'overall_desc' | 'overall_asc' | 'power_desc' | 'contact_desc' | 'speed_desc' | 'age_asc' | 'age_desc' | 'newest';

const POSITIONS = ['All', 'P', 'C', '1B', '2B', 'SS', '3B', 'OF'];
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'newest', label: 'Newest First' },
  { key: 'price_asc', label: 'Price: Low → High' },
  { key: 'price_desc', label: 'Price: High → Low' },
  { key: 'overall_desc', label: 'Overall: Best First' },
  { key: 'overall_asc', label: 'Overall: Worst First' },
  { key: 'power_desc', label: 'Power: Highest' },
  { key: 'contact_desc', label: 'Contact: Highest' },
  { key: 'speed_desc', label: 'Speed: Highest' },
  { key: 'age_asc', label: 'Age: Youngest' },
  { key: 'age_desc', label: 'Age: Oldest' },
];

const MarketplacePage: React.FC<{ userData?: any }> = ({ userData }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [positionFilter, setPositionFilter] = useState('All');
  const [handedFilter, setHandedFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => { loadListings(); }, []);

  async function loadListings() {
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }
    
    // Get active listings
    const { data: listingData } = await supabase!
      .from('marketplace_listings')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    
    if (!listingData?.length) { setListings([]); setLoading(false); return; }
    
    // Batch fetch player data
    const playerIds = listingData.map(l => l.player_id);
    const playerData: any[] = [];
    for (let i = 0; i < playerIds.length; i += 200) {
      const batch = playerIds.slice(i, i + 200);
      const { data } = await supabase!.from('players')
        .select('id, first_name, last_name, position, age, gender, overall, power, contact, speed, discipline, batting_hand, throwing_hand, velocity, control, movement, user_id')
        .in('id', batch);
      if (data) playerData.push(...data);
    }
    const playerMap = new Map(playerData.map(p => [p.id, p]));
    
    // Batch fetch seller names
    const sellerIds = Array.from(new Set(listingData.map(l => l.seller_id)));
    const { data: sellerData } = await supabase!.from('users')
      .select('id, username')
      .in('id', sellerIds);
    const sellerMap = new Map((sellerData || []).map(s => [s.id, s.username]));
    
    // Merge
    const merged = listingData.map(l => {
      const player = playerMap.get(l.player_id) || {};
      return {
        ...l,
        first_name: player.first_name,
        last_name: player.last_name,
        position: player.position,
        age: player.age,
        gender: player.gender,
        overall: player.overall,
        power: player.power,
        contact: player.contact,
        speed: player.speed,
        discipline: player.discipline,
        batting_hand: player.batting_hand,
        throwing_hand: player.throwing_hand,
        velocity: player.velocity,
        control: player.control,
        movement: player.movement,
        seller_name: sellerMap.get(l.seller_id) || 'Unknown',
      };
    }).filter(l => l.first_name); // Only show if player data exists
    
    setListings(merged);
    setLoading(false);
  }

  const filtered = listings.filter(l => {
    if (positionFilter !== 'All' && l.position !== positionFilter) return false;
    if (handedFilter === 'RHB' && l.batting_hand !== 'R') return false;
    if (handedFilter === 'LHB' && l.batting_hand !== 'L') return false;
    if (handedFilter === 'SH' && l.batting_hand !== 'S') return false;
    if (genderFilter === 'male' && l.gender !== 'male') return false;
    if (genderFilter === 'female' && l.gender !== 'female') return false;
    if (minPrice && l.price < parseInt(minPrice)) return false;
    if (maxPrice && l.price > parseInt(maxPrice)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = `${l.first_name} ${l.last_name}`.toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortKey) {
      case 'price_asc': return a.price - b.price;
      case 'price_desc': return b.price - a.price;
      case 'overall_desc': return (b.overall || 0) - (a.overall || 0);
      case 'overall_asc': return (a.overall || 0) - (b.overall || 0);
      case 'power_desc': return (b.power || 0) - (a.power || 0);
      case 'contact_desc': return (b.contact || 0) - (a.contact || 0);
      case 'speed_desc': return (b.speed || 0) - (a.speed || 0);
      case 'age_asc': return (a.age || 0) - (b.age || 0);
      case 'age_desc': return (b.age || 0) - (a.age || 0);
      case 'newest': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      default: return 0;
    }
  });

  const isPitcher = (pos?: string) => pos?.toUpperCase() === 'P';

  return (
    <div className="marketplace-page">
      <h1>🏪 Marketplace</h1>
      <p className="subtitle">Buy and sell players. Forge material or team upgrades.</p>

      <div className="mp-filters">
        <div className="mp-view-toggle">
          <button className={`mp-toggle ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>▦ Grid</button>
          <button className={`mp-toggle ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>☰ List</button>
        </div>
        <input 
          type="text" 
          placeholder="🔍 Search player name..." 
          value={searchQuery} 
          onChange={e => setSearchQuery(e.target.value)}
          className="mp-search"
        />
        <select value={positionFilter} onChange={e => setPositionFilter(e.target.value)}>
          {POSITIONS.map(p => <option key={p} value={p}>{p === 'All' ? 'All Positions' : p}</option>)}
        </select>
        <select value={handedFilter} onChange={e => setHandedFilter(e.target.value)}>
          <option value="All">All Handedness</option>
          <option value="RHB">Right-Handed Bat</option>
          <option value="LHB">Left-Handed Bat</option>
          <option value="SH">Switch Hitter</option>
        </select>
        <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)}>
          <option value="All">♂♀ All</option>
          <option value="male">♂ Male</option>
          <option value="female">♀ Female</option>
        </select>
        <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}>
          {SORT_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <div className="mp-price-range">
          <input type="number" placeholder="Min $" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
          <span>—</span>
          <input type="number" placeholder="Max $" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
        </div>
      </div>

      <div className="mp-results-count">
        {filtered.length} player{filtered.length !== 1 ? 's' : ''} listed
      </div>

      {loading ? (
        <p>Loading marketplace...</p>
      ) : sorted.length === 0 ? (
        <div className="mp-empty">
          <p>No players listed yet. Be the first to sell!</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="mp-grid">
          {sorted.map(l => (
            <div key={l.id} className={`mp-card ${l.gender === 'female' ? 'mp-female' : l.gender === 'male' ? 'mp-male' : ''}`} onClick={() => navigate(`/player/${l.player_id}`)}>
              <div className="mp-card-body">
                <div className="mp-card-top">
                  <div className="mp-card-image">
                    {(l as any).avatar_url ? (
                      <img src={(l as any).avatar_url} alt={`${l.first_name} ${l.last_name}`} />
                    ) : (
                      <div className="mp-card-image-fallback">
                        {l.first_name?.[0]}{l.last_name?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="mp-card-info">
                    <div className="mp-name">{l.first_name} {l.last_name}</div>
                    <div className="mp-card-meta">
                      <span className={`mp-pos-badge ${isPitcher(l.position) ? 'pitcher' : ['SS','2B','3B','1B'].includes(l.position || '') ? 'infield' : l.position === 'C' ? 'catcher' : 'outfield'}`}>{l.position}</span>
                      <span>Age {l.age}</span>
                      <span className="mp-gender">{l.gender === 'female' ? '♀' : l.gender === 'male' ? '♂' : ''}</span>
                      <span>{l.batting_hand === 'S' ? 'SH' : l.batting_hand === 'L' ? 'LHB' : 'RHB'}/{l.throwing_hand === 'L' ? 'LHP' : 'RHP'}</span>
                    </div>
                  </div>
                  <div className="mp-ovr">{numberToGrade(l.overall)}</div>
                </div>
                <div className="mp-card-attrs">
                  {isPitcher(l.position) ? (
                    <>
                      <div className="mp-attr"><span className="mp-attr-label">VEL</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.velocity || 0)}%`, background: '#f87171' }}></div></div><span className="mp-attr-value">{l.velocity || '-'}</span></div>
                      <div className="mp-attr"><span className="mp-attr-label">CTL</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.control || 0)}%`, background: '#60a5fa' }}></div></div><span className="mp-attr-value">{l.control || '-'}</span></div>
                      <div className="mp-attr"><span className="mp-attr-label">MOV</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.movement || 0)}%`, background: '#a78bfa' }}></div></div><span className="mp-attr-value">{l.movement || '-'}</span></div>
                      <div className="mp-attr"><span className="mp-attr-label">DIS</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.discipline || 0)}%`, background: '#4ade80' }}></div></div><span className="mp-attr-value">{l.discipline || '-'}</span></div>
                    </>
                  ) : (
                    <>
                      <div className="mp-attr"><span className="mp-attr-label">POW</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.power || 0)}%`, background: '#f87171' }}></div></div><span className="mp-attr-value">{l.power || '-'}</span></div>
                      <div className="mp-attr"><span className="mp-attr-label">CON</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.contact || 0)}%`, background: '#60a5fa' }}></div></div><span className="mp-attr-value">{l.contact || '-'}</span></div>
                      <div className="mp-attr"><span className="mp-attr-label">SPD</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.speed || 0)}%`, background: '#fbbf24' }}></div></div><span className="mp-attr-value">{l.speed || '-'}</span></div>
                      <div className="mp-attr"><span className="mp-attr-label">DIS</span><div className="mp-attr-bar"><div className="mp-attr-fill" style={{ width: `${(l.discipline || 0)}%`, background: '#4ade80' }}></div></div><span className="mp-attr-value">{l.discipline || '-'}</span></div>
                    </>
                  )}
                </div>
                <div className="mp-card-footer">
                  <span className="mp-price">💰 {l.price.toLocaleString()}</span>
                  <span className="mp-seller">by {l.seller_name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mp-list">
          <table className="mp-list-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Pos</th>
                <th>Age</th>
                <th>Gen</th>
                <th>OVR</th>
                <th>Pow</th>
                <th>Con</th>
                <th>Spd</th>
                <th>Dis</th>
                <th>Bat/Throw</th>
                <th>Price</th>
                <th>Seller</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(l => (
                <tr key={l.id} onClick={() => navigate(`/player/${l.player_id}`)}>
                  <td>{l.first_name} {l.last_name}</td>
                  <td>{l.position}</td>
                  <td>{l.age}</td>
                  <td>{l.gender === 'female' ? '♀' : l.gender === 'male' ? '♂' : '-'}</td>
                  <td>{numberToGrade(l.overall)}</td>
                  <td>{isPitcher(l.position) ? l.velocity || '-' : l.power || '-'}</td>
                  <td>{isPitcher(l.position) ? l.control || '-' : l.contact || '-'}</td>
                  <td>{l.speed || '-'}</td>
                  <td>{l.discipline || '-'}</td>
                  <td>{l.batting_hand === 'S' ? 'SH' : l.batting_hand === 'L' ? 'LHB' : 'RHB'}/{l.throwing_hand === 'L' ? 'LHP' : 'RHP'}</td>
                  <td className="mp-price">{l.price.toLocaleString()}</td>
                  <td className="mp-seller">{l.seller_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;
